import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Target, TrendingUp, Calendar, Users, Rocket, Building2 } from 'lucide-react';
import NavigationControls from '../components/NavigationControls';
import PartieBadge from '../components/PartieBadge';
import { useLanguage } from '../contexts/LanguageContext';
import { financialData, yearlyTotals, objectifs, TAM_TOTAL, TRAITEURS_INIT, equipeCommerciale, preSeedInvestment, tractionActuelle } from '../mock';
import { formatCurrency } from '../lib/utils';

function getGtmTableData() {
  const years = Object.keys(financialData);
  return years.map((yearLabel, idx) => {
    const months = financialData[yearLabel];
    const first = months[0];
    const ecolesStart = first?.ecolesTotal || 0;
    const repasStart = first?.repasReguliers || 0;
    const ecolesFin = Math.max(...months.map(m => m.ecolesTotal || 0));
    const repasFin = Math.max(...months.map(m => m.repasReguliers || 0));
    return { yearLabel, ecolesStart, ecolesFin, repasStart, repasFin, idx };
  });
}

const provincesParAnnee = (lang) => ({
  0: ['QC'],
  1: ['QC'],
  2: ['QC', 'ON', lang === 'fr' ? 'Privé' : 'Private'],
  3: ['QC', 'ON', 'AB', lang === 'fr' ? 'Privé' : 'Private'],
  4: ['QC', 'ON', 'AB', 'BC', 'MB', 'SK', lang === 'fr' ? 'Privé' : 'Private'],
  5: ['QC', 'ON', 'AB', 'BC', 'MB', 'SK', 'NB', 'NE', 'TNL', 'IPE', lang === 'fr' ? 'Privé' : 'Private'],
});

export default function GTMPage() {
  const { language } = useLanguage();
  const tableData = getGtmTableData();

  const jalons = language === 'fr' ? [
    { date: 'Avr 2026', label: `Pre-launch — ${TRAITEURS_INIT} traiteurs + ~200 écoles en onboarding`, color: 'gray', icon: Rocket },
    { date: 'Sep 2026', label: 'Y1 — Premiers repas scolaires QC (~10K repas/jour)', color: 'lime', icon: Target },
    { date: 'Sep 2027', label: 'Y2 — Entrée Ontario + croissance QC', color: 'cyan', icon: TrendingUp },
    { date: 'Sep 2028', label: 'Y3 — Entrée Alberta + ON agressif', color: 'orange', icon: Building2 },
    { date: 'Sep 2029', label: 'Y4 — + BC, Manitoba, Saskatchewan (6 provinces)', color: 'purple', icon: Users },
    { date: 'Sep 2030', label: `Y5 — Canada complet (10 provinces), ${objectifs.total.toLocaleString('fr-CA')} écoles`, color: 'lime', icon: Target },
  ] : [
    { date: 'Apr 2026', label: `Pre-launch — ${TRAITEURS_INIT} caterers + ~200 schools onboarding`, color: 'gray', icon: Rocket },
    { date: 'Sep 2026', label: 'Y1 — First school meals QC (~10K meals/day)', color: 'lime', icon: Target },
    { date: 'Sep 2027', label: 'Y2 — Ontario entry + QC growth', color: 'cyan', icon: TrendingUp },
    { date: 'Sep 2028', label: 'Y3 — Alberta entry + aggressive ON', color: 'orange', icon: Building2 },
    { date: 'Sep 2029', label: 'Y4 — + BC, Manitoba, Saskatchewan (6 provinces)', color: 'purple', icon: Users },
    { date: 'Sep 2030', label: `Y5 — Full Canada (10 provinces), ${objectifs.total.toLocaleString('en-CA')} schools`, color: 'lime', icon: Target },
  ];

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto">
        <PartieBadge path="/gtm" />
        <Card className="bg-black/60 border-lime-500/30 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-2xl sm:text-3xl lg:text-4xl text-white mb-2">
              Go-to-Market
            </CardTitle>
            <div className="h-1 w-16 sm:w-24 bg-gradient-to-r from-lime-500 to-cyan-500 rounded-full" />
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Pré-seed Structure Commerciale */}
            <div className="p-6 bg-gradient-to-br from-lime-500/15 via-cyan-500/10 to-transparent rounded-xl border-2 border-lime-500/40" data-testid="gtm-pre-seed">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-lime-500 text-black text-[10px] font-bold rounded-full uppercase tracking-wider">
                  {language === 'fr' ? 'Pré-seed 500K — 6 mois' : 'Pre-seed $500K — 6 months'}
                </span>
                <span className="text-gray-300 text-sm">{language === 'fr' ? 'Sept → Fév' : 'Sep → Feb'}</span>
              </div>
              <h3 className="text-xl font-bold text-lime-400 mb-4">
                {language === 'fr' ? 'Structure Commerciale' : 'Commercial Structure'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="space-y-2">
                  <div className="flex justify-between p-2 bg-black/40 rounded">
                    <span className="text-gray-300 text-sm">{language === 'fr' ? `${equipeCommerciale.vendeurs} vendeurs` : `${equipeCommerciale.vendeurs} sales reps`}</span>
                    <span className="text-lime-400 text-sm font-semibold">{language === 'fr' ? 'Calls, qualification, signature (pas de démos)' : 'Calls, qualification, signing (no demos)'}</span>
                  </div>
                  <div className="flex justify-between p-2 bg-black/40 rounded">
                    <span className="text-gray-300 text-sm">{language === 'fr' ? 'Top Closer Québec' : 'Top Closer Quebec'}</span>
                    <span className="text-cyan-400 text-sm font-semibold">{language === 'fr' ? 'Closing, Programme Fondateur' : 'Closing, Founder Program'}</span>
                  </div>
                  <div className="flex justify-between p-2 bg-black/40 rounded">
                    <span className="text-gray-300 text-sm">{language === 'fr' ? 'Directeur des ventes' : 'Sales Director'}</span>
                    <span className="text-orange-400 text-sm font-semibold">{language === 'fr' ? 'Démos & support closing' : 'Demos & closing support'}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between p-2 bg-black/40 rounded">
                    <span className="text-gray-300 text-sm">{language === 'fr' ? 'Lead Provider' : 'Lead Provider'}</span>
                    <span className="text-purple-400 text-sm font-semibold">{language === 'fr' ? 'Listes qualifiées / priorisation' : 'Qualified lists / prioritization'}</span>
                  </div>
                  <div className="flex justify-between p-2 bg-black/40 rounded">
                    <span className="text-gray-300 text-sm">{language === 'fr' ? 'Support onboarding' : 'Onboarding support'}</span>
                    <span className="text-lime-300 text-sm font-semibold">{language === 'fr' ? 'Onboarding / SAV / suivi' : 'Onboarding / service / follow-up'}</span>
                  </div>
                  <div className="flex justify-between p-2 bg-black/40 rounded border border-lime-500/30">
                    <span className="text-gray-300 text-sm font-semibold">{language === 'fr' ? 'Cadence' : 'Cadence'}</span>
                    <span className="text-lime-400 text-sm font-bold">{language === 'fr' ? `${equipeCommerciale.cadenceParVendeur} traiteurs/vendeur/mois` : `${equipeCommerciale.cadenceParVendeur} caterers/rep/month`}</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-black/40 rounded-lg border border-lime-500/30 text-center">
                  <p className="text-xs text-gray-400 uppercase">{language === 'fr' ? 'Cible 6 mois' : '6-month target'}</p>
                  <p className="text-2xl font-bold text-lime-400">{equipeCommerciale.totalTraiteurs6Mois}-100</p>
                  <p className="text-xs text-gray-500 mt-1">{language === 'fr' ? 'traiteurs signés' : 'caterers signed'}</p>
                </div>
                <div className="p-3 bg-black/40 rounded-lg border border-cyan-500/30 text-center">
                  <p className="text-xs text-gray-400 uppercase">CAC</p>
                  <p className="text-2xl font-bold text-cyan-400">3 500-3 889 $</p>
                  <p className="text-xs text-gray-500 mt-1">{language === 'fr' ? 'par traiteur (GTM direct)' : 'per caterer (direct GTM)'}</p>
                </div>
                <div className="p-3 bg-black/40 rounded-lg border border-orange-500/30 text-center">
                  <p className="text-xs text-gray-400 uppercase">{language === 'fr' ? 'Coût par étab.' : 'Cost per inst.'}</p>
                  <p className="text-2xl font-bold text-orange-400">233-259 $</p>
                  <p className="text-xs text-gray-500 mt-1">{language === 'fr' ? '1 500 établissements' : '1,500 institutions'}</p>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-4 border-t border-lime-500/20 pt-3">
                {language === 'fr'
                  ? `Traction actuelle : ${tractionActuelle.traiteurs} traiteurs, ${tractionActuelle.ecoles} écoles + ${tractionActuelle.garderies} garderies signées en ${tractionActuelle.duree}.`
                  : `Current traction: ${tractionActuelle.traiteurs} caterers, ${tractionActuelle.ecoles} schools + ${tractionActuelle.garderies} daycares signed in ${tractionActuelle.dureeEn}.`}
              </p>
            </div>

            {/* Stratégie Domination Québec */}
            <div className="p-6 bg-gradient-to-br from-purple-500/15 via-cyan-500/10 to-transparent rounded-xl border-2 border-purple-500/40" data-testid="gtm-qc-strategy">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-purple-500 text-white text-[10px] font-bold rounded-full uppercase tracking-wider">
                  {language === 'fr' ? 'Stratégie de Conquête' : 'Conquest Strategy'}
                </span>
              </div>
              <h3 className="text-xl font-bold text-purple-400 mb-3">
                {language === 'fr' ? 'Dominer le Québec en premier, ensuite le Canada' : 'Dominate Quebec first, then Canada'}
              </h3>
              <p className="text-gray-300 text-sm leading-relaxed mb-3">
                {language === 'fr'
                  ? 'EcoLunch concentre 100% de son effort commercial pré-seed au Québec. La logique : prouver l\'efficacité opérationnelle, accumuler des données réelles, gagner la notoriété, et établir des références institutionnelles solides. Une fois le marché québécois consolidé, l\'accès aux autres provinces (Ontario, Alberta, etc.) devient drastiquement plus facile — autant en notoriété qu\'en preuves commerciales.'
                  : 'EcoLunch focuses 100% of its pre-seed commercial effort in Quebec. The rationale: prove operational efficiency, accumulate real data, build brand recognition, and establish strong institutional references. Once the Quebec market is consolidated, access to other provinces (Ontario, Alberta, etc.) becomes dramatically easier — both in notoriety and commercial proof.'}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                <div className="p-3 bg-black/40 rounded-lg border border-purple-500/30">
                  <p className="text-xs text-purple-300 font-semibold uppercase tracking-wider mb-1">{language === 'fr' ? 'Étape 1 — Pré-seed' : 'Step 1 — Pre-seed'}</p>
                  <p className="text-sm text-gray-300">{language === 'fr' ? '100% Québec • Domination du marché scolaire + garderies' : '100% Quebec • School + daycare market domination'}</p>
                </div>
                <div className="p-3 bg-black/40 rounded-lg border border-cyan-500/30">
                  <p className="text-xs text-cyan-300 font-semibold uppercase tracking-wider mb-1">{language === 'fr' ? 'Étape 2 — Post-seed' : 'Step 2 — Post-seed'}</p>
                  <p className="text-sm text-gray-300">{language === 'fr' ? 'Expansion Ontario + autres provinces avec preuves QC' : 'Ontario + other provinces expansion with QC proof'}</p>
                </div>
                <div className="p-3 bg-black/40 rounded-lg border border-lime-500/30">
                  <p className="text-xs text-lime-300 font-semibold uppercase tracking-wider mb-1">{language === 'fr' ? 'Avantage' : 'Advantage'}</p>
                  <p className="text-sm text-gray-300">{language === 'fr' ? 'Notoriété + données réelles + références = closing rapide ailleurs' : 'Brand recognition + real data + references = rapid closing elsewhere'}</p>
                </div>
              </div>
            </div>


            {/* Pénétration TAM par scénario Y1 */}
            <div data-testid="gtm-penetration">
              <h3 className="text-xl font-bold text-lime-400 mb-4">
                {language === 'fr' ? 'Pénétration du TAM — Scénarios Année 1' : 'TAM Penetration — Year 1 Scenarios'}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left p-3 text-gray-400 font-semibold">{language === 'fr' ? 'Scénario' : 'Scenario'}</th>
                      <th className="text-right p-3 text-gray-400 font-semibold">{language === 'fr' ? 'Écoles' : 'Schools'}</th>
                      <th className="text-right p-3 text-gray-400 font-semibold">% TAM</th>
                      <th className="text-right p-3 text-gray-400 font-semibold">{language === 'fr' ? 'Garderies' : 'Daycares'}</th>
                      <th className="text-right p-3 text-gray-400 font-semibold">% TAM Ext.</th>
                      <th className="text-right p-3 text-gray-400 font-semibold">% Traiteurs</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-800">
                      <td className="p-3 text-cyan-400 font-semibold">{language === 'fr' ? 'Minimum M6' : 'Minimum M6'}</td>
                      <td className="p-3 text-right text-gray-300">500</td>
                      <td className="p-3 text-right text-cyan-400">3,9%</td>
                      <td className="p-3 text-right text-gray-300">500</td>
                      <td className="p-3 text-right text-cyan-400">5,8%</td>
                      <td className="p-3 text-right text-cyan-400">4,7%</td>
                    </tr>
                    <tr className="border-b border-gray-800">
                      <td className="p-3 text-cyan-400 font-semibold">{language === 'fr' ? 'Minimum M12' : 'Minimum M12'}</td>
                      <td className="p-3 text-right text-gray-300">1 000</td>
                      <td className="p-3 text-right text-cyan-400">7,9%</td>
                      <td className="p-3 text-right text-gray-300">1 000</td>
                      <td className="p-3 text-right text-cyan-400">11,7%</td>
                      <td className="p-3 text-right text-cyan-400">9,4%</td>
                    </tr>
                    <tr className="border-b border-gray-800 bg-lime-500/5">
                      <td className="p-3 text-lime-400 font-bold">{language === 'fr' ? 'Réaliste M6' : 'Realistic M6'}</td>
                      <td className="p-3 text-right text-gray-300">750</td>
                      <td className="p-3 text-right text-lime-400 font-bold">5,9%</td>
                      <td className="p-3 text-right text-gray-300">750</td>
                      <td className="p-3 text-right text-lime-400 font-bold">8,7%</td>
                      <td className="p-3 text-right text-lime-400 font-bold">7,0%</td>
                    </tr>
                    <tr className="bg-lime-500/10">
                      <td className="p-3 text-lime-400 font-bold">{language === 'fr' ? 'Réaliste M12' : 'Realistic M12'}</td>
                      <td className="p-3 text-right text-gray-300">1 500</td>
                      <td className="p-3 text-right text-lime-400 font-bold">11,8%</td>
                      <td className="p-3 text-right text-gray-300">1 500</td>
                      <td className="p-3 text-right text-lime-400 font-bold">17,5%</td>
                      <td className="p-3 text-right text-lime-400 font-bold">14,1%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                {language === 'fr'
                  ? '% TAM = part du marché écoles (12 700) ou garderies externes (429 132 places ÷ 50 enfants) couverte. % Traiteurs = part des 1 419 traiteurs-équivalents.'
                  : 'TAM % = share of schools market (12,700) or external daycares (429,132 places ÷ 50 children). Caterers % = share of 1,419 caterer-equivalents.'}
              </p>
            </div>

            {/* Jalons stratégiques 6 mois */}
            <div data-testid="gtm-milestones">
              <h3 className="text-xl font-bold text-cyan-400 mb-4">
                {language === 'fr' ? 'Jalons Stratégiques (6 mois)' : 'Strategic Milestones (6 months)'}
              </h3>
              <div className="space-y-3">
                {[
                  { date: language === 'fr' ? 'Septembre M1' : 'September M1', label: language === 'fr' ? 'Activation équipe + démarrage cadence 15 traiteurs/mois' : 'Team activation + 15 caterers/month cadence start', color: 'lime' },
                  { date: language === 'fr' ? 'Octobre-Novembre M2-M3' : 'October-November M2-M3', label: language === 'fr' ? 'Scaling commercial — 158-344 écoles + 141-331 garderies' : 'Commercial scaling — 158-344 schools + 141-331 daycares', color: 'cyan' },
                  { date: language === 'fr' ? 'Décembre M4' : 'December M4', label: language === 'fr' ? 'Break-even mensuel approché (rev ~76-122K $)' : 'Monthly break-even approached (rev ~$76-122K)', color: 'orange' },
                  { date: language === 'fr' ? 'Janvier M5' : 'January M5', label: language === 'fr' ? 'Premier mois positif — préparation matériaux investisseurs' : 'First positive month — investor materials prep', color: 'purple' },
                  { date: language === 'fr' ? 'Février M6 — Pivot stratégique' : 'February M6 — Strategic pivot', label: language === 'fr' ? 'Objectifs atteints (Min 500+500 / Réaliste 750+750) — lancement ronde seed' : 'Targets reached (Min 500+500 / Realistic 750+750) — seed round launch', color: 'lime' },
                ].map((j, idx) => (
                  <div key={idx} className={`flex items-center gap-4 p-3 bg-black/40 rounded-lg border border-${j.color}-500/30`}>
                    <div className={`w-10 h-10 rounded-full bg-${j.color}-500/20 flex items-center justify-center flex-shrink-0`}>
                      <span className={`text-${j.color}-400 font-bold text-sm`}>M{idx + 1 + (idx >= 4 ? 1 : 0)}</span>
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-bold text-${j.color}-400`}>{j.date}</p>
                      <p className="text-sm text-gray-300">{j.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>


            <NavigationControls />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
